import { defineStore } from 'pinia'
import axios from '@/services/index.js'
import myaxios from 'axios'
import { useMessagesStore } from './messages'

export const useDownloadsStore = defineStore('downloads', {
  modelName: 'Download',
  state: () => ({
    downloads: [],
    loading: false,
    error: null
  }),

  getters: {
    showDownloads: (state) => state.downloads,
    isLoading: (state) => state.loading,
    hasError: (state) => state.error !== null
  },

  actions: {
    onModelChanged() {
      this.getDownloads()
    },

    async getDownloads() {
      this.loading = true
      this.error = null

      try {
        const response = await axios.post('/api/downloads/list')
        this.downloads = response.data.data || []
        return this.downloads
      } catch (error) {
        this.error = error.response?.data?.message || 'Error al cargar descargas'
        this.downloads = []
        throw error
      } finally {
        this.loading = false
      }
    },

    // Obtener URL presignada de S3 para descarga
    async getPresignedUrl(downloadId) {
      try {
        const response = await axios.post(`/api/downloads/getPresigned/${downloadId}`)

        if (response.data && response.data.data) {
          return response.data.data // URL presignada del archivo
        }

        throw new Error('No se pudo obtener la URL de descarga')
      } catch (error) {
        this.error = error.response?.data?.message || 'Error al obtener URL de descarga'
        throw error
      }
    },

    /**
     * Descarga (o abre) el PDF que devuelve un endpoint de la API.
     *
     * Es la ÚNICA implementación: la usan el botón del detalle —a través de
     * `DownloadDoc`— y el del listado. Antes cada uno tenía la suya, parecidas
     * pero no iguales, y la del listado llamaba a un método del store de
     * sucursales que no existe: reventaba en la primera línea, el catch se lo
     * tragaba y el usuario veía "hubo un problema" sin más.
     *
     * El endpoint devuelve una URL firmada, no el archivo: por eso son dos
     * pasos, y el segundo va con un axios **sin interceptores**, porque el
     * nuestro le agrega el header de autorización y el bucket rechaza una
     * URL firmada que además venga autenticada.
     */
    async descargarPdf({ endpoint, filename, abrir = false }) {
      const url = await this.getPdfUrl(endpoint)

      const sinInterceptores = myaxios.create()
      const respuesta = await sinInterceptores.get(url, { responseType: 'blob' })

      const blob = new Blob([respuesta.data], { type: 'application/pdf' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename

      if (abrir) {
        window.open(link.href, '_blank') || window.location.replace(link.href)
      } else {
        link.click()
      }

      URL.revokeObjectURL(link.href)
    },

    // Método genérico para obtener URL de PDF desde cualquier endpoint
    async getPdfUrl(endpoint) {
      try {
        const response = await axios.get(endpoint)

        if (response.data) {
          // El backend puede retornar la URL directamente o dentro de un objeto
          return response.data.url || response.data.data || response.data
        }

        throw new Error('No se pudo obtener la URL del documento')
      } catch (error) {
        this.error = error.response?.data?.message || 'Error al obtener URL del documento'
        throw error
      }
    },

    emitSuccessAlert() {
      const messagesStore = useMessagesStore()
      setTimeout(() => {
        messagesStore.showSuccess('Su descarga está lista')
      }, 1000)
    },

    // Limpiar errores
    clearError() {
      this.error = null
    }
  }
})